import { useEffect, useMemo, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { toBillingInfo, summarizeBilling } from '../../lib/billing';
import { euros } from '../../lib/performance';
import {
  MAINTENANCE_CENTS,
  monthKey,
  monthLabelFr,
  selectableMonths,
  tenantsForMonth,
  monthTotals,
  monthlyHistory,
  invoiceRef,
  type MonthlyStatRaw,
  type TenantMonth,
} from '../../lib/monthlyBilling';
import {
  loadMonthlyStats,
  loadInvoiceLog,
  sendMonthlyInvoice,
  resetInvoiceToDraft,
  cancelInvoice,
  type InvoiceLogEntry,
} from '../../lib/monthlyBillingService';
import { createMonthlyInvoice } from '../../lib/commissionsService';
import { loadInvoicePdf, downloadBase64Pdf } from '../../lib/odooInvoices';

/**
 * « Facturation Lil & Co » — pilotage par MOIS CIVIL :
 *   • sélecteur de mois (12 derniers) ;
 *   • CA commissions du mois : encaissé Stripe (en ligne) vs à facturer (sur place) ;
 *   • par resto : maintenance 128 € HT (si actif) + commissions sur place = TOTAL À FACTURER ;
 *   • statut de la facture (envoyée / à générer) + actions : brouillon Odoo,
 *     ou Générer + VALIDER + ENVOYER (email PDF au client, confirmation en 2 temps) ;
 *   • historique du CA commissions mois par mois (évolution).
 * Le super admin calcule tout ; Odoo ne fait que produire la facture.
 */

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR');
};

export default function MonthlyBillingSection({ tenants }: { tenants: Tenant[] }) {
  const [rows, setRows] = useState<MonthlyStatRaw[]>([]);
  const [log, setLog] = useState<Map<string, InvoiceLogEntry>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<string>(monthKey(new Date()));
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmSlug, setConfirmSlug] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, string>>({});

  const refresh = async () => {
    const [stats, logMap] = await Promise.all([loadMonthlyStats(), loadInvoiceLog()]);
    if (stats.ok) { setRows(stats.rows); setError(null); }
    else setError(stats.error ?? 'Chargement impossible.');
    setLog(logMap);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const activeSlugs = useMemo(
    () => new Set(tenants.filter((t) => summarizeBilling(toBillingInfo(t)).status === 'active').map((t) => t.slug)),
    [tenants],
  );
  const months = useMemo(() => selectableMonths(rows), [rows]);
  const perTenant = useMemo(
    () => tenantsForMonth(rows, month, activeSlugs, tenants.map((t) => ({ slug: t.slug, name: t.name }))),
    [rows, month, activeSlugs, tenants],
  );
  const totals = useMemo(() => monthTotals(perTenant), [perTenant]);
  const history = useMemo(() => monthlyHistory(rows), [rows]);
  const maxHistCents = Math.max(1, ...history.map((h) => h.commissionCents));

  const [year, monthNum] = month.split('-').map(Number);
  const currentKey = monthKey(new Date());
  const isCurrentMonth = month === currentKey;

  const doDraft = async (t: TenantMonth) => {
    setBusy(t.slug);
    const res = await createMonthlyInvoice(t.slug, year, monthNum);
    setMsg((m) => ({ ...m, [t.slug]: res.message }));
    setBusy(null);
  };

  const doSend = async (t: TenantMonth) => {
    setConfirmSlug(null);
    setBusy(t.slug);
    const res = await sendMonthlyInvoice(t.slug, year, monthNum);
    setMsg((m) => ({ ...m, [t.slug]: res.message }));
    setBusy(null);
    if (res.ok) await refresh();
  };

  const doPdf = async (t: TenantMonth, entry: InvoiceLogEntry) => {
    if (!entry.invoice_id) return;
    setBusy(t.slug);
    const res = await loadInvoicePdf(t.slug, entry.invoice_id);
    if (res.ok && res.pdfBase64) downloadBase64Pdf(res.pdfBase64, res.filename ?? `${entry.invoice_name ?? 'facture'}.pdf`);
    else setMsg((m) => ({ ...m, [t.slug]: res.error ?? 'PDF indisponible.' }));
    setBusy(null);
  };

  const doDraftReset = async (t: TenantMonth, entry: InvoiceLogEntry) => {
    if (!entry.invoice_id) return;
    setBusy(t.slug);
    const res = await resetInvoiceToDraft(entry.invoice_id);
    setMsg((m) => ({ ...m, [t.slug]: res.message }));
    setBusy(null);
  };

  const doCancel = async (t: TenantMonth, entry: InvoiceLogEntry) => {
    setConfirmSlug(null);
    if (!entry.invoice_id) return;
    setBusy(t.slug);
    const res = await cancelInvoice(entry.invoice_id, invoiceRef(t.slug, month));
    setMsg((m) => ({ ...m, [t.slug]: res.message }));
    setBusy(null);
    if (res.ok) await refresh();
  };

  return (
    <div className="max-w-5xl">
      {/* En-tête + sélecteur de mois civil */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Facturation Lil &amp; Co</h1>
          <p className="text-sm opacity-70">
            Maintenance {euros(MAINTENANCE_CENTS)} HT/mois + commissions 0,99 € par commande site · TVA 20 %.
            En ligne = encaissé Stripe · Sur place = à facturer au resto.
          </p>
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold"
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabelFr(m)}{m === currentKey ? ' (en cours)' : ''}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div>
      ) : loading ? (
        <div className="py-6 text-center text-sm text-slate-400">Chargement…</div>
      ) : (
        <>
          {/* KPIs du mois sélectionné */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase text-slate-500">💶 CA commissions ({monthLabelFr(month)})</div>
              <div className="mt-1 text-2xl font-black text-slate-900">{euros(totals.commissionCents)}</div>
              <div className="text-xs text-slate-500">{totals.onlineOrders + totals.onsiteOrders} commande(s) site</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs font-bold uppercase text-emerald-700">✅ Encaissé (Stripe)</div>
              <div className="mt-1 text-2xl font-black text-emerald-800">{euros(totals.onlineCents)}</div>
              <div className="text-xs text-emerald-700/80">{totals.onlineOrders} cmd payée(s) en ligne</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs font-bold uppercase text-amber-700">🧾 Commissions sur place</div>
              <div className="mt-1 text-2xl font-black text-amber-800">{euros(totals.onsiteCents)}</div>
              <div className="text-xs text-amber-700/80">{totals.onsiteOrders} cmd payée(s) au resto</div>
            </div>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="text-xs font-bold uppercase text-indigo-700">📤 Total à facturer (HT)</div>
              <div className="mt-1 text-2xl font-black text-indigo-800">{euros(totals.toInvoiceCents)}</div>
              <div className="text-xs text-indigo-700/80">maintenance {euros(totals.maintenanceCents)} + commissions {euros(totals.onsiteCents)}</div>
            </div>
          </div>

          {/* Détail par resto */}
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="px-2 py-2">Restaurant</th>
                  <th className="px-2 py-2 text-right">En ligne</th>
                  <th className="px-2 py-2 text-right">Sur place</th>
                  <th className="px-2 py-2 text-right">Maintenance</th>
                  <th className="px-2 py-2 text-right">À facturer (HT)</th>
                  <th className="px-2 py-2">Facture</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {perTenant.map((t) => {
                  const entry = log.get(invoiceRef(t.slug, month));
                  const sent = entry?.status === 'sent';
                  return (
                    <tr key={t.slug} className="border-b border-slate-100 align-top">
                      <td className="px-2 py-2 font-semibold text-slate-700">{t.name}</td>
                      <td className="px-2 py-2 text-right text-emerald-700">
                        {euros(t.onlineCents)}
                        <div className="text-[11px] text-slate-400">{t.onlineOrders} cmd</div>
                      </td>
                      <td className="px-2 py-2 text-right text-amber-700">
                        {euros(t.onsiteCents)}
                        <div className="text-[11px] text-slate-400">{t.onsiteOrders} cmd</div>
                      </td>
                      <td className="px-2 py-2 text-right text-slate-700">
                        {t.maintenanceCents > 0 ? euros(t.maintenanceCents) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-indigo-800">{euros(t.toInvoiceCents)}</td>
                      <td className="px-2 py-2">
                        {sent ? (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                            {entry?.invoice_name ?? 'Envoyée'} · {fmtDate(entry!.sent_at)}
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">À générer</span>
                        )}
                        {sent && entry?.sent_to && (
                          <div className="mt-1 text-[11px] text-slate-400">→ {entry.sent_to}</div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {sent && entry && (
                          <div className="flex flex-col items-end gap-1">
                            {confirmSlug === `cancel:${t.slug}` ? (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => doCancel(t, entry)}
                                  disabled={busy === t.slug}
                                  className="rounded-lg bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                  Confirmer l'annulation
                                </button>
                                <button
                                  onClick={() => setConfirmSlug(null)}
                                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
                                >
                                  Garder
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => doPdf(t, entry)}
                                  disabled={busy === t.slug || !entry.invoice_id}
                                  title="Télécharger le PDF de la facture"
                                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {busy === t.slug ? '…' : '📄 PDF'}
                                </button>
                                <button
                                  onClick={() => doDraftReset(t, entry)}
                                  disabled={busy === t.slug || !entry.invoice_id}
                                  title="Repasser en brouillon dans Odoo pour la modifier (puis re-valider dans Odoo)"
                                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                                >
                                  ↩️ Modifier
                                </button>
                                <button
                                  onClick={() => setConfirmSlug(`cancel:${t.slug}`)}
                                  disabled={busy === t.slug || !entry.invoice_id}
                                  title="Annuler la facture dans Odoo et permettre une refacturation du mois"
                                  className="rounded-lg border border-red-300 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                  🗑 Annuler
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {!sent && (
                          <div className="flex flex-col items-end gap-1">
                            {confirmSlug === t.slug ? (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => doSend(t)}
                                  disabled={busy === t.slug}
                                  className="rounded-lg bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                  Confirmer l'envoi au client
                                </button>
                                <button
                                  onClick={() => setConfirmSlug(null)}
                                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
                                >
                                  Annuler
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => doDraft(t)}
                                  disabled={busy === t.slug}
                                  title="Créer le brouillon dans Odoo, sans envoi"
                                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {busy === t.slug ? '…' : '🧾 Brouillon'}
                                </button>
                                <button
                                  onClick={() => setConfirmSlug(t.slug)}
                                  disabled={busy === t.slug || t.toInvoiceCents === 0}
                                  title="Générer + valider dans Odoo + envoyer le PDF par email au resto"
                                  className="rounded-lg bg-indigo-600 px-2 py-1 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                                >
                                  📧 Générer &amp; envoyer
                                </button>
                              </div>
                            )}
                            {isCurrentMonth && (
                              <div className="text-[11px] text-amber-600">⚠ mois en cours (incomplet)</div>
                            )}
                          </div>
                        )}
                        {msg[t.slug] && <div className="mt-1 max-w-[220px] text-[11px] text-slate-500">{msg[t.slug]}</div>}
                      </td>
                    </tr>
                  );
                })}
                {perTenant.length === 0 && (
                  <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400">Aucune commande ni resto actif sur ce mois.</td></tr>
                )}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-slate-400">
              L'envoi automatique tourne le 1er de chaque mois à 08h30 (mois précédent). Ici tu peux générer à la demande —
              une facture déjà envoyée n'est jamais renvoyée (journal anti-doublon).
            </p>
          </div>

          {/* Historique : évolution du CA commissions */}
          {history.length > 0 && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">📈 Évolution du CA commissions</div>
              <div className="flex flex-col gap-2">
                {history.map((h) => (
                  <button
                    key={h.month}
                    onClick={() => setMonth(h.month)}
                    className={`flex items-center gap-3 rounded-lg px-2 py-1 text-left hover:bg-slate-50 ${h.month === month ? 'bg-indigo-50' : ''}`}
                  >
                    <span className="w-28 shrink-0 text-xs font-semibold text-slate-600">{monthLabelFr(h.month)}</span>
                    <span className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
                      <span className="flex h-full">
                        <span className="h-full bg-emerald-400" style={{ width: `${(h.onlineCents / maxHistCents) * 100}%` }} />
                        <span className="h-full bg-amber-400" style={{ width: `${(h.onsiteCents / maxHistCents) * 100}%` }} />
                      </span>
                    </span>
                    <span className="w-24 shrink-0 text-right text-sm font-bold text-slate-800">{euros(h.commissionCents)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2 flex gap-4 text-[11px] text-slate-500">
                <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-400" />Encaissé Stripe (en ligne)</span>
                <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-400" />À facturer (sur place)</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
